import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  SearchIcon, 
  ClipboardListIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  Clock10Icon,
  AlertCircleIcon,
  FilterIcon,
  SlidersHorizontalIcon,
  SendIcon
} from "lucide-react";
import { RFQService, RFQRequest, RFQResponse } from "@/lib/rfq-service";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

export default function SupplierRFQDashboard() {
  const [openRfqs, setOpenRfqs] = useState<RFQRequest[]>([]);
  const [myResponses, setMyResponses] = useState<RFQResponse[]>([]);
  const [filteredRfqs, setFilteredRfqs] = useState<RFQRequest[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<RFQResponse[]>([]);
  const [loadingOpen, setLoadingOpen] = useState(true);
  const [loadingResponses, setLoadingResponses] = useState(true);
  const [activeTab, setActiveTab] = useState("available");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const rfqService = new RFQService();
  
  useEffect(() => {
    const loadRFQs = async () => {
      if (!user) return;
      
      try {
        setLoadingOpen(true);
        const availableRfqs = await rfqService.getOpenRFQsForSupplier(user.id);
        setOpenRfqs(availableRfqs);
        setFilteredRfqs(availableRfqs);
      } catch (error) {
        console.error("Error loading available RFQs:", error);
        toast({
          title: "Error",
          description: "Failed to load available RFQs. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoadingOpen(false);
      }
    };
    
    const loadResponses = async () => {
      if (!user) return;
      
      try {
        setLoadingResponses(true);
        const responses = await rfqService.getSupplierResponses(user.id);
        setMyResponses(responses);
        setFilteredResponses(responses);
      } catch (error) {
        console.error("Error loading your responses:", error);
        toast({
          title: "Error",
          description: "Failed to load your quote responses. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoadingResponses(false);
      }
    };
    
    loadRFQs();
    loadResponses();
  }, [user]);
  
  // Apply filters when tab or search changes
  useEffect(() => {
    if (activeTab === "available" && openRfqs.length > 0) {
      let filtered = [...openRfqs];
      
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          rfq => rfq.title.toLowerCase().includes(query) || 
                rfq.description.toLowerCase().includes(query)
        );
      }
      
      // Apply category filter
      if (categoryFilter && categoryFilter !== "all") {
        filtered = filtered.filter(rfq => rfq.category_id === categoryFilter);
      }
      
      setFilteredRfqs(filtered);
    } else if (activeTab === "my-quotes" && myResponses.length > 0) {
      let filtered = [...myResponses];
      
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          response => 
            response.rfq_requests?.title.toLowerCase().includes(query) || 
            response.rfq_requests?.description.toLowerCase().includes(query)
        );
      }
      
      setFilteredResponses(filtered);
    }
  }, [activeTab, searchQuery, categoryFilter, openRfqs, myResponses]);
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline" className="flex items-center gap-1">
          <Clock10Icon className="h-3.5 w-3.5" />
          Draft
        </Badge>;
      case "submitted":
        return <Badge className="bg-blue-500 flex items-center gap-1">
          <SendIcon className="h-3.5 w-3.5" />
          Submitted
        </Badge>;
      case "accepted":
        return <Badge className="bg-green-500 flex items-center gap-1">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Accepted
        </Badge>;
      case "rejected":
        return <Badge className="bg-red-500 flex items-center gap-1">
          <XCircleIcon className="h-3.5 w-3.5" />
          Rejected
        </Badge>;
      case "negotiating":
        return <Badge className="bg-yellow-500 flex items-center gap-1">
          <AlertCircleIcon className="h-3.5 w-3.5" />
          Negotiating
        </Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };
  
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: currency || 'USD' 
    }).format(amount);
  };
  
  const getDaysRemaining = (expiryDate?: string) => {
    if (!expiryDate) return "No deadline";
    
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "Expired";
    if (diffDays === 0) return "Expires today";
    if (diffDays === 1) return "1 day left";
    return `${diffDays} days left`;
  };
  
  if (!user) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert>
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>
            Please <Link to="/login" className="font-medium underline">log in</Link> as a supplier to view available RFQs.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Supplier Quote Management</h1>
        <p className="text-gray-500 mt-1">Respond to RFQs and manage your quotes</p>
      </div>
      
      <div className="mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="relative flex-grow">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder={activeTab === "available" ? "Search available RFQs..." : "Search your quotes..."}
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {activeTab === "available" && (
            <div className="flex gap-2 items-center">
              <SlidersHorizontalIcon className="h-4 w-4 text-gray-500" />
              <Select 
                value={categoryFilter} 
                onValueChange={setCategoryFilter}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {/* TODO: Populate with actual categories from system */}
                  <SelectItem value="cat1">Construction Equipment</SelectItem>
                  <SelectItem value="cat2">Mining Machinery</SelectItem>
                  <SelectItem value="cat3">Agricultural Equipment</SelectItem>
                </SelectContent>
              </Select>
              
              {(searchQuery || categoryFilter !== "all") && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setCategoryFilter("all");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="available">
            Available RFQs
          </TabsTrigger>
          <TabsTrigger value="my-quotes">
            My Quotes
          </TabsTrigger>
        </TabsList>
        
        {/* Available RFQs Tab */}
        <TabsContent value="available">
          {loadingOpen ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-4 w-1/4 mt-2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3 mt-2" />
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-24" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : filteredRfqs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardListIcon className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-medium mb-2">No RFQs Available</h3>
                <p className="text-gray-500 text-center mb-6">
                  {searchQuery || categoryFilter
                    ? "No RFQs match your current filters. Try adjusting your search criteria."
                    : "There are no open RFQs in your categories at the moment. Check back later or update your supplier profile to receive more opportunities."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredRfqs.map((rfq) => (
                <Card key={rfq.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{rfq.title}</CardTitle>
                        <CardDescription>
                          Posted on {formatDate(rfq.created_at)}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className={`
                        ${getDaysRemaining(rfq.expiry_date).includes("Expired") 
                          ? "border-red-300 text-red-600" 
                          : getDaysRemaining(rfq.expiry_date).includes("day") 
                            ? "border-yellow-300 text-yellow-600"
                            : "border-green-300 text-green-600"
                        }
                      `}>
                        {getDaysRemaining(rfq.expiry_date)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="line-clamp-2 text-gray-600">
                      {rfq.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
                      <div>
                        <span className="text-gray-500">Quantity:</span>{" "}
                        <span className="font-medium">{rfq.quantity}</span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Budget:</span>{" "}
                        <span className="font-medium">
                          {rfq.budget_min && rfq.budget_max 
                            ? `$${rfq.budget_min} - $${rfq.budget_max}`
                            : "Not specified"
                          }
                        </span>
                      </div>
                      
                      {rfq.delivery_date_required && (
                        <div>
                          <span className="text-gray-500">Delivery needed by:</span>{" "}
                          <span className="font-medium">{formatDate(rfq.delivery_date_required)}</span>
                        </div>
                      )}
                      
                      {rfq.delivery_location && (
                        <div>
                          <span className="text-gray-500">Location:</span>{" "}
                          <span className="font-medium">{rfq.delivery_location}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4 flex justify-between">
                    <Button
                      onClick={() => navigate(`/supplier-dashboard/rfqs/${rfq.id}`)}
                    >
                      View Details & Respond
                    </Button>
                    
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500">
                        Category: <span className="font-medium">
                          {rfq.category_id /* TODO: Replace with actual category name */}
                        </span>
                      </span>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* My Quotes Tab */}
        <TabsContent value="my-quotes">
          {loadingResponses ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-4 w-1/4 mt-2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3 mt-2" />
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-9 w-24" />
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : filteredResponses.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardListIcon className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-medium mb-2">No Quote Responses</h3>
                <p className="text-gray-500 text-center mb-6">
                  {searchQuery
                    ? "No quotes match your search criteria. Try adjusting your search."
                    : "You haven't submitted any quotes yet. Browse available RFQs to get started."}
                </p>
                {!searchQuery && (
                  <Button 
                    onClick={() => setActiveTab("available")}
                    variant="outline"
                  >
                    Browse Available RFQs
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredResponses.map((response) => (
                <Card key={response.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{response.rfq_requests?.title || "Untitled RFQ"}</CardTitle>
                        <CardDescription>
                          Quote submitted on {formatDate(response.created_at)}
                        </CardDescription>
                      </div>
                      <div>
                        {getStatusBadge(response.status)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">Your Quote:</span>{" "}
                        <span className="font-medium">{formatCurrency(response.quoted_price, response.currency)}</span>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Delivery Date:</span>{" "}
                        <span className="font-medium">{formatDate(response.delivery_date)}</span>
                      </div>
                      
                      {response.delivery_fee && (
                        <div>
                          <span className="text-gray-500">Delivery Fee:</span>{" "}
                          <span className="font-medium">{formatCurrency(response.delivery_fee, response.currency)}</span>
                        </div>
                      )}
                      
                      {response.validity_period && (
                        <div>
                          <span className="text-gray-500">Quote valid until:</span>{" "}
                          <span className="font-medium">{response.validity_period}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="border-t pt-4 flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/supplier-dashboard/quotes/${response.id}`)}
                    >
                      View Quote Details
                    </Button>
                    
                    {response.status === "negotiating" && (
                      <Button
                        onClick={() => navigate(`/supplier-dashboard/quotes/${response.id}/edit`)}
                      >
                        Update Quote
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
